import { parseEther } from 'ethers'
import { useEffect, useState } from 'react'

import { Box } from '@chakra-ui/react'
import Link from 'next/link'
import { formatEther, formatUnits } from 'viem'
import { avalanche } from 'viem/chains'
import { useAccount, useBalance, useWatchAsset } from 'wagmi'

import IconInfo from '../IconInfo/IconInfo'
import { StakeForm } from '../LiquidStaking'
import styles from './StakingForm.module.scss'
import StakingPill from './StakingPill/StakingPill'
import StakingToggle from './StakingToggle/StakingToggle'
import ApproveAVAXButton from '../Wizard/components/ApproveAVAXButton'

import { Address } from '@/common/components/Address'
import { Tooltip } from '@/common/components/Tooltip'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import {
  useBalanceOf,
  useConvertToAssets,
  useMaxDeposit,
  useMaxRedeem,
} from '@/hooks/useAvaxBalanceOfggAvax'
import { useCoinGeckoPrices } from '@/hooks/useCoinGeckoPrices'
import {
  useAPYinBasis,
  usePendingRewards,
  useUnderlyingBalance,
  useVaultCap,
  useVaultRatio,
  useVaultTotalAssets,
} from '@/hooks/useGetVaultData'
import { useRewards } from '@/hooks/useRewards'
import { USDValue, formatNumber } from '@/utils/numberFormatter'

export default function StakingForm() {
  const { chain, address: account } = useAccount()
  const { currentToken } = useValueTokens()
  const { data: apy } = useAPYinBasis()
  const { data: vaultTotalAssets } = useVaultTotalAssets()
  const { prices: coingeckoPrices } = useCoinGeckoPrices()
  const { data: maxDeposit } = useMaxDeposit()
  const { data: maxRedeem } = useMaxRedeem()
  const { data: vaultCap } = useVaultCap()
  const { data: pendingRewards } = usePendingRewards()
  const { watchAsset } = useWatchAsset()
  const { data: underlyingBalnce } = useUnderlyingBalance()
  const { data: ratio } = useVaultRatio()
  const ratioDecimal = ratio && parseFloat(formatUnits(ratio, 18))
  const [amount, setAmount] = useState<bigint>(0n)
  const [toggle, setToggle] = useState<'stake' | 'unstake'>('stake')
  const [amountGreaterThanCap, setAmountGreaterThanCap] = useState(false)
  const [amountGreaterThanPool, setAmountGreaterThanPool] = useState(false)

  const apyInString = (apy ? Number(apy) / 100 : 0).toString() + ' %'
  const GGPAddress = currentToken.tokenAddresses[0][chain?.id || avalanche.id] as `0x${string}`
  const xGGPAddress = currentToken.tokenAddresses[1][chain?.id || avalanche.id] as `0x${string}`

  const { data: balance } = useBalance({
    address: account,
    token: GGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  const { data: ggAVAXBalance } = useBalance({
    address: account,
    token: xGGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  const { data: balanceOf } = useBalanceOf()

  const { data: convertToAssetsData } = useConvertToAssets(balanceOf || 0n)
  const { data: maxRedeemToAssetsData } = useConvertToAssets(maxRedeem)

  const { rewards } = useRewards(account, ratioDecimal, ggAVAXBalance?.value || 0n)

  const additional =
    vaultTotalAssets && pendingRewards && ggAVAXBalance?.value
      ? (BigInt(ggAVAXBalance?.value) * BigInt(pendingRewards)) / BigInt(vaultTotalAssets)
      : 0n

  const rewardsToShow = formatEther(BigInt(rewards.toFixed(0)) + additional || 0n)

  const clearAmount = () => {
    setAmount(0n)
  }

  useEffect(() => {
    if (maxDeposit) {
      setAmountGreaterThanCap(amount > maxDeposit)
    } else {
      setAmountGreaterThanCap(amount > 0n)
    }
  }, [amount, maxDeposit])

  useEffect(() => {
    if (maxRedeemToAssetsData) {
      setAmountGreaterThanPool(amount > maxRedeemToAssetsData)
    } else {
      setAmountGreaterThanPool(amount > 0n)
    }
  }, [amount, maxRedeemToAssetsData])

  return (
    <div className={styles.container}>
      <StakingToggle onChange={setToggle} />

      <div className={styles.staking}>
        <h1 className={styles.staking__title}>
          {currentToken.displayName} Vault{' '}
          <Tooltip
            content="This Vault strategy uses deposited AVAX to run GoGoPool minipools. The AVAX is staked onto SeaFi's minipools, used for 15 days, then returned to the Vault with yield. This earns depositors a fixed yield. Liquidity is re-added to the vault for 1 day after each cycle, giving depositors constant liquidity if they need to remove their AVAX"
            placement="right"
          >
            <IconInfo className="size-6" />
          </Tooltip>
        </h1>
        <div className={styles.staking__grid}>
          <StakingPill
            label="APY"
            tooltip="Rewards are accumulated automatically every few days"
            value={apyInString}
          />

          <StakingPill
            label="Token Address"
            value={
              <Box alignItems="center" display="flex">
                <Address copyable fontWeight="black">
                  {xGGPAddress}
                </Address>
                <a
                  href={`https://snowtrace.io/token/${xGGPAddress}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Box as="span" className="ml-2" fontWeight="bold">
                    →
                  </Box>
                </a>
              </Box>
            }
          />

          <StakingPill
            label="Vault TVL"
            smallValue={`($${vaultTotalAssets ? USDValue(formatEther(vaultTotalAssets), coingeckoPrices, currentToken.value) : '0.00'})`}
            value={`${vaultTotalAssets ? formatNumber(formatEther(vaultTotalAssets), 0) : '0.00'} ${currentToken.displayName}`}
          />

          <StakingPill
            label="seaAVAX Pool"
            tooltip="The avaliable liquidity that can be withdrawn from the Vault. AVAX is redeposited into the Vault every few days as nodes spin down"
            value={`${underlyingBalnce ? formatNumber(formatEther(underlyingBalnce), 0) : '0.00'} ${currentToken.displayName}`}
          />

          <div className={styles.staking__input}>
            {toggle === 'stake' ? (
              <StakeForm
                amount={amount}
                balance={balance?.value || 0n}
                greaterThanPool={amountGreaterThanCap}
                header="Amount to deposit"
                max={maxDeposit}
                setAmount={setAmount}
                toggle={toggle}
                token={`${currentToken.displayName}`}
              />
            ) : (
              <StakeForm
                amount={amount}
                balance={convertToAssetsData || parseEther('0')}
                greaterThanPool={amountGreaterThanPool}
                header="Amount to withdraw"
                max={maxRedeemToAssetsData}
                setAmount={setAmount}
                toggle={toggle}
                token={`${currentToken.displayName}`}
              />
            )}
          </div>

          <StakingPill
            fullWidth
            label="Your Rewards"
            value={`${rewardsToShow ? formatNumber(rewardsToShow, 6) : '0.00'} ${currentToken.displayName}`}
          />

          <StakingPill
            fullWidth
            label="AVAX Vault Cap"
            value={`${vaultCap ? formatNumber(formatEther(vaultCap), 0) : '0.00'} ${currentToken.displayName}`}
          />
        </div>
        <div className={styles.staking__submit}>
          <ApproveAVAXButton
            amount={amount}
            amountGreaterThanCap={amountGreaterThanCap}
            amountGreaterThanPool={amountGreaterThanPool}
            balance={toggle === 'stake' ? balance?.value || 0n : convertToAssetsData || 0n}
            clearAmount={clearAmount}
            maxDeposit={maxDeposit}
            swapDirection={toggle === 'unstake'}
          />

          <Link
            className={styles.staking__link}
            href="#"
            onClick={() => {
              watchAsset({
                type: 'ERC20',
                options: {
                  address: xGGPAddress,
                  symbol: `sea${currentToken.displayName}`,
                  decimals: 18,
                },
              })
            }}
          >
            Add sea{currentToken.displayName} token to wallet
          </Link>
        </div>
      </div>
    </div>
  )
}
